import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import {
  findingName,
  findingSentence,
} from "@/profitability/review/finding-row";
import { STATUS_LABEL, RESOLUTION_LABEL } from "@/profitability/review/labels";
import { useFindings } from "@/profitability/review/query";
import type { FindingFilter } from "@/profitability/review/types";
import { REVIEW_FILTERS } from "@/profitability/review/use-review-queue";
import { profitabilityTemplatePath } from "@/profitability/routes";
import type {
  ReviewRule,
  TemplateSettings,
} from "@/profitability/settings/types";
import { reportError } from "@/profitability/utils";
import { RulePeriodSelect } from "./rule-period";

export const RuleFindings = ({
  businessId,
  reportId,
  rule,
  periods,
  runId,
  onPeriodChange,
}: {
  businessId: string;
  reportId: string;
  rule: Pick<ReviewRule, "id" | "on_label"> & {
    track?: Pick<ReviewRule["track"], "evaluation">;
  };
  periods: NonNullable<TemplateSettings["periods"]>;
  runId: string | null;
  onPeriodChange: (id: string) => void;
}) => {
  const [filter, setFilter] = useState<FindingFilter>("all");
  const findings = useFindings(
    businessId,
    reportId,
    filter,
    { ruleId: rule.id, ...(runId ? { runId } : {}) },
    { enabled: !!runId },
  );
  const scope = JSON.stringify([rule.id, runId, filter]);
  const [position, setPosition] = useState({ scope, page: 0 });
  if (position.scope !== scope) setPosition({ scope, page: 0 });
  const pages = findings.data?.pages ?? [];
  const page =
    position.scope === scope
      ? Math.min(position.page, Math.max(pages.length - 1, 0))
      : 0;
  const rows = pages[page]?.findings ?? [];
  const nextPage = async () => {
    if (!pages[page + 1]) {
      const next = await findings.fetchNextPage();
      if (next.isError) return;
    }
    setPosition({ scope, page: page + 1 });
  };
  return (
    <Stack render={<section aria-labelledby="rule-findings-title" />} gap={3}>
      <Stack gap={3}>
        <Heading level={2} variant="title" id="rule-findings-title">
          Flagged records
        </Heading>
        <Stack
          gap={3}
          className="sm:flex-row sm:items-center sm:justify-between"
        >
          <RulePeriodSelect
            periods={periods}
            runId={runId}
            onChange={onPeriodChange}
          />
          <Select
            value={filter}
            items={Object.fromEntries(
              REVIEW_FILTERS.map((item) => [item.value, item.label]),
            )}
            onValueChange={(value) => {
              const selected = REVIEW_FILTERS.find(
                (item) => item.value === value,
              );
              if (selected) setFilter(selected.value);
            }}
          >
            <SelectTrigger
              aria-label="Finding status"
              className="w-full sm:w-36"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
              {REVIEW_FILTERS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
        <Text variant="caption" tone="muted">
          Figures are from this period; review status is current.
          {runId &&
            rule.track?.evaluation?.[runId] === "partial" &&
            " Only some records were evaluated."}
        </Text>
      </Stack>
      {findings.isError &&
        rows.length > 0 &&
        !findings.isFetchNextPageError && (
          <Stack gap={2} role="alert">
            <Text tone="destructive">
              These findings could not refresh. Showing the last loaded results.
            </Text>
            <Button
              variant="outline"
              className="self-start"
              loading={findings.isFetching}
              onClick={() => void findings.refetch()}
            >
              Try again
            </Button>
          </Stack>
        )}
      {!runId ? (
        <Text tone="muted">Run a report to see what this rule flags.</Text>
      ) : findings.isPending ? (
        <Skeleton
          className="h-32 w-full"
          role="status"
          aria-label="Loading flagged records"
        />
      ) : findings.isError && rows.length === 0 ? (
        <Stack gap={2} role="alert">
          <Text tone="destructive">{reportError(findings.error)}</Text>
          <Button
            variant="outline"
            className="self-start"
            loading={findings.isFetching}
            onClick={() => void findings.refetch()}
          >
            Try again
          </Button>
        </Stack>
      ) : rows.length === 0 ? (
        <Stack gap={1} className="rounded-lg border border-line p-5">
          <Text variant="label">
            {filter === "all"
              ? "No records flagged in this period"
              : `No ${filter === "open" ? "active" : "closed"} findings in this period`}
          </Text>
          <Text tone="muted">
            {rule.track?.evaluation?.[runId] === "disabled"
              ? "This rule was off for this period."
              : rule.track?.evaluation?.[runId] === "not_evaluated"
                ? "This rule was not evaluated for this period."
                : "Choose another period or status to explore its findings."}
          </Text>
        </Stack>
      ) : (
        <Stack className="divide-y divide-line rounded-lg border border-line">
          {rows.map((finding) => {
            const params = new URLSearchParams({
              filter: "all",
              record: finding.dataset,
              record_id: finding.record_id,
            });
            if (runId) params.set("period", runId);
            return (
              <Link
                key={finding.id}
                state={{ focusFinding: true }}
                to={`${profitabilityTemplatePath(reportId)}/review?${params}`}
                className="flex flex-col gap-2 rounded-md p-4 hover:bg-neutral-subtle focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink"
              >
                <Stack
                  gap={2}
                  className="sm:flex-row sm:items-start sm:justify-between"
                >
                  <Text
                    variant="label"
                    className="min-w-0 [overflow-wrap:anywhere]"
                  >
                    {findingName(finding) || rule.on_label || "Flagged record"}
                  </Text>
                  <Badge
                    variant="outline"
                    size="sm"
                    className="shrink-0 self-start"
                  >
                    {finding.resolution
                      ? RESOLUTION_LABEL[finding.resolution]
                      : STATUS_LABEL[finding.status]}
                  </Badge>
                </Stack>
                <Text className="text-pretty [overflow-wrap:anywhere]">
                  {findingSentence(finding)}
                </Text>
                {finding.subtitle && (
                  <Text variant="caption" tone="muted">
                    {finding.subtitle}
                  </Text>
                )}
                <Text variant="caption" tone="muted">
                  View finding →
                </Text>
              </Link>
            );
          })}
        </Stack>
      )}
      {findings.isFetchNextPageError && (
        <Text role="alert" tone="destructive">
          More findings could not load. Try again.
        </Text>
      )}
      {(page > 0 || findings.hasNextPage || pages[page + 1]) && (
        <Stack direction="row" align="center" justify="between" gap={2}>
          <Button
            variant="outline"
            disabled={page === 0 || findings.isFetchingNextPage}
            onClick={() => setPosition({ scope, page: page - 1 })}
          >
            Previous
          </Button>
          <Text role="status" variant="caption" tone="muted" tabular>
            Page {page + 1}
          </Text>
          <Button
            variant="outline"
            disabled={!pages[page + 1] && !findings.hasNextPage}
            loading={findings.isFetchingNextPage}
            onClick={() => void nextPage()}
          >
            {findings.isFetchNextPageError ? "Try again" : "Next"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
